/**
 * The Admin area (Phase 7b, container): its section links and the section's
 * page. Users, groups, sites, device secrets and the audit need `tenant.admin`
 * (tenant-wide); usage needs `billing.view`. The tenant admin surface is the
 * cloud's: from a node's page its calls go straight to the cloud (host-aware
 * routing), and where they cannot (no cloud, an offline session, the cloud
 * down) the area says so instead of failing section by section.
 */
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { useGrants, useRouteAvailability, useServices } from '../../app/hooks.js';
import { shellAdminSections } from '../../lib/console.js';
import { AdminNav, type AdminSection } from '../../components/admin/AdminNav.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card.js';
import { adminSections } from '../../lib/admin/sections.js';
import { CloudOnlyGate } from '../CloudOnlyPage.js';

export function AdminLayout() {
  const grants = useGrants();
  const { shell } = useServices();
  const pathname = useLocation({ select: (l) => l.pathname });
  const navigate = useNavigate();
  const admin = useRouteAvailability('listUsers');

  // The tenant admin surface is the cloud's: from a node's page its calls go to the cloud (`connectivity/routing.ts`),
  // and where they cannot, the area says so once instead of failing section by section.
  if (!admin.ok) {
    return (
      <CloudOnlyGate route="listUsers" what="Admin" path={pathname}>
        {null}
      </CloudOnlyGate>
    );
  }

  // D39: in the staff console the Admin area is its Staff section — the staff tenant's users and groups only.
  const sections: AdminSection[] = shellAdminSections(adminSections(grants), shell).map((s) => ({ ...s, active: pathname === s.href || pathname.startsWith(`${s.href}/`) }));
  if (sections.length === 0) {
    return (
      <Card className="mx-auto max-w-2xl" data-testid="admin-forbidden">
        <CardHeader>
          <CardTitle>No admin access</CardTitle>
          <CardDescription>The Admin area needs tenant administration (or, for usage, billing) permission.</CardDescription>
        </CardHeader>
        <CardContent />
      </Card>
    );
  }
  return (
    <div className="mx-auto grid max-w-6xl gap-4" data-testid="admin-area">
      <AdminNav
        sections={sections}
        onNavigate={(href) => {
          void navigate({ to: href });
        }}
      />
      <Outlet />
    </div>
  );
}
